import { Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import useGetSeasonalInsights from '@/services/api/cash-flow/useGetSeasonalInsights'

interface NeverForecastInsight {
  type: 'never_forecast'
  transaction_category_id: number
  category: string
  direction: 'in' | 'out'
  reference_year: number
  reference_month: string
  amount: number
}

interface ChronicBiasInsight {
  type: 'chronic_bias'
  transaction_category_id: number
  category: string
  direction: 'over' | 'under'
  mean_pct: number
  years: number[]
  tracking_same_way: boolean
}

type Insight = NeverForecastInsight | ChronicBiasInsight

function money(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`
}

function IconBubble({ tint, children }: { tint: string; children: React.ReactNode }) {
  return (
    <div
      className="flex size-5 shrink-0 items-center justify-center rounded-md"
      style={{ background: tint }}
    >
      {children}
    </div>
  )
}

function NeverForecastRow({ insight }: { insight: NeverForecastInsight }) {
  const verb = insight.direction === 'out' ? 'went out on' : 'came in from'

  return (
    <div className="flex items-start gap-2.5">
      <IconBubble tint="hsla(261,92%,72%,.16)">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M6 1.6v4.7l3.2 1.9"
            stroke="hsl(261,56%,38%)"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="6" cy="6" r="4.6" stroke="hsl(261,56%,38%)" strokeWidth="1.2" />
        </svg>
      </IconBubble>
      <div className="min-w-0 flex-1 text-xs leading-normal text-txt-secondary">
        Last {insight.reference_month}{' '}
        <span className="font-semibold text-txt-primary">{money(insight.amount)}</span>{' '}
        {verb} {insight.category} with no estimate set. Nothing is planned this month
        either.{' '}
        <Link to="/table" className="font-semibold text-txt-accent">
          Add estimate
        </Link>
      </div>
    </div>
  )
}

function ChronicBiasRow({ insight }: { insight: ChronicBiasInsight }) {
  const years = insight.years.join(' and ')

  return (
    <div className="flex items-start gap-2.5">
      <IconBubble tint="hsla(44,66%,50%,.16)">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M1.8 8.6l2.8-2.9 2 1.6 3.6-4.1"
            stroke="hsl(40,100%,30%)"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconBubble>
      <div className="min-w-0 flex-1 text-xs leading-normal text-txt-secondary">
        {insight.category} ran{' '}
        <span className="font-semibold text-txt-primary">
          {insight.mean_pct}% {insight.direction} plan
        </span>{' '}
        in {years}.
        {insight.tracking_same_way && ' This month is tracking the same way.'}
      </div>
    </div>
  )
}

/**
 * "Same period, earlier years" — what this month's history says about the
 * month currently open.
 *
 * Sits where the score breakdown used to: the six-month bars say how accurate
 * forecasting has been, this says what to do about the month in progress.
 */
export default function ForecastInsights() {
  const { data, status } = useGetSeasonalInsights({ limit: 3 })

  const insights: Insight[] = data?.data?.insights ?? []

  if (status === 'error') return null
  if (status === 'success' && insights.length === 0) return null

  return (
    <div className="flex flex-col gap-2.5 border-t border-light pt-3">
      <div className="flex items-baseline justify-between gap-2.5">
        <div className="text-[12.5px] font-semibold text-txt-primary">Insights</div>
        <div className="text-[11.5px] text-txt-secondary">
          same period, earlier years
        </div>
      </div>

      {status === 'pending' && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-4/5" />
        </div>
      )}

      {status === 'success' &&
        insights.map((insight) =>
          insight.type === 'never_forecast' ? (
            <NeverForecastRow
              key={`nf-${insight.transaction_category_id}`}
              insight={insight}
            />
          ) : (
            <ChronicBiasRow
              key={`cb-${insight.transaction_category_id}`}
              insight={insight}
            />
          ),
        )}
    </div>
  )
}
